import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";

import { Trans } from "react-i18next";
import React from "react";
import { Box } from "@mui/material";
import { InternalAssessmentApiRow } from "../InternalAssessments.types";
import useCloseInternalAssessmentDialogHooks from "./CloseInternalAssessmentDialog.hooks";
import { useTranslation } from "../../localization/useTranslation";
import { getEnumValues } from "../../common/enumOperations";
import { LegalEntityAuditClosedReason } from "../../legalEntity/legalEntityAuditTab/closeAuditDialog/CloseAuditDialog.types";
import { setFirstLetterToLowerCase } from "../../common/stringOperations";
import HighlightedName from "@common/components/HighlightedName";

// An auditor's report is a third party's statement standing in for the recipient answering the questionnaire,
// which only makes sense for an external audit. The external dialog also attaches the report as a document;
// this one has no upload, so the reason could never carry the report it names.
const closableReasons = getEnumValues(LegalEntityAuditClosedReason).filter((reason) => reason !== LegalEntityAuditClosedReason.AuditOpinion);

export interface ICloseInternalAssessmentDialog {
    onDialogClose: () => void;
    onInternalAssessmentClosed: () => void;
    selectedInternalAssessment: InternalAssessmentApiRow;
}

function CloseInternalAssessmentDialog(props: ICloseInternalAssessmentDialog) {
    const { translateString, translateDate } = useTranslation();
    const { saveModel, onStatusChange, onSendNotificationToAssigneeChange, validation, onCloseClick, isSaving } = useCloseInternalAssessmentDialogHooks(props);

    validation.check(saveModel);

    return (
        <DotLegalDialog
            buttonOkText={translateString("close")}
            header={translateString("closeInternalAssessment")}
            size="sm"
            open
            onDialogClose={props.onDialogClose}
            onOkClick={() => onCloseClick()}
            inProgress={isSaving}
            disableBackdropOnCloseIfValueChange={saveModel}
        >
            <React.Fragment>
                <DotLegalHeader fontWeight={400} center headerStyle="small">
                    <Trans
                        i18nKey={"closeInternalAssessmentDialogContent"}
                        values={{
                            name: props.selectedInternalAssessment.name.trim(),
                            deadline: translateDate(props.selectedInternalAssessment.deadline),
                        }}
                        components={{
                            wrapper: <HighlightedName />,
                        }}
                    />
                </DotLegalHeader>
                <DotLegalSelect
                    options={closableReasons.map((x) => {
                        return { name: translateString(setFirstLetterToLowerCase(LegalEntityAuditClosedReason[x].toString())), id: x.toString() };
                    })}
                    onChange={(value) => onStatusChange(Number(value))}
                    label={translateString("reason")}
                    noOptionsLabel=""
                    placeholder={translateString("reason")}
                    selectedItem={saveModel.status !== undefined ? saveModel.status.toString() : ""}
                    disableClearable
                    disableSearch
                    errorText={validation.getErrorsForDisplay("status")}
                />
                {!props.selectedInternalAssessment.assigneeDeleted && (
                    <Box sx={{ marginLeft: "-12px" }}>
                        <DotLegalCheckbox
                            label={translateString("sendNotificationToAssignee")}
                            checked={saveModel.sendNotificationToAssignee}
                            onChange={onSendNotificationToAssigneeChange}
                        />
                    </Box>
                )}
            </React.Fragment>
        </DotLegalDialog>
    );
}

export default CloseInternalAssessmentDialog;
